import { Injectable } from "@angular/core";
import { BaseApiService } from "./base.service";
import { Observable } from "rxjs";
import { ConversationMessage } from "../models/message";
import { SessionSummary, SessionTitle, UserSession } from "../models/session";

@Injectable({
    providedIn: "root",
})
export class SessionsService extends BaseApiService {
    protected readonly resourcePath = "sessions";

    /** One conversation by its own id. Reachable only to a host whose credential may review
     *  conversations it did not hold, which is not how the widget reads its own. */
    getConversation(sessionId: string): Observable<ConversationMessage[]> {
        return this._http.get<ConversationMessage[]>(this._baseUrl + `/${sessionId}/conversation`);
    }

    /**
     * One conversation by client token. The public counterpart of `getConversation`, and the one
     * the chatbot itself uses: the sidebar addresses conversations by token, not by session id.
     */
    getClientSession(userId: string, clientToken: string): Observable<ConversationMessage[]> {
        return this._http.get<ConversationMessage[]>(this._baseUrl + `/${userId}/${clientToken}`);
    }

    /** Every conversation of a user, transcripts included. Heavy: see `getSessionSummaries`. */
    getAllClientSessions(userId: string): Observable<UserSession[]> {
        return this._http.get<UserSession[]>(this._baseUrl + `/${userId}`);
    }

    /**
     * The sidebar's list: one row per conversation, without the messages.
     *
     * `getAllClientSessions` above answers the same question by downloading every transcript,
     * which is fine for a host reviewing them and far too much to draw a list of titles.
     */
    getSessionSummaries(userId: string): Observable<SessionSummary[]> {
        return this._http.get<SessionSummary[]>(this._baseUrl + `/${userId}/summary`);
    }

    /**
     * Renames one conversation. The backend trims the title and echoes back what it stored, so the
     * caller should apply the response rather than the string it sent.
     */
    renameSession(sessionId: string, title: string): Observable<SessionTitle> {
        return this._http.patch<SessionTitle>(this._baseUrl + `/${sessionId}`, { title });
    }

    /** Soft-deletes one conversation: it leaves the user's history, the messages are kept. */
    deleteSession(sessionId: string): Observable<void> {
        return this._http.delete<void>(this._baseUrl + `/${sessionId}`);
    }
}
